// Profile Image Upload Component
// Handles profile and logo image uploads with preview and validation

import { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { uploadProfileImage, uploadLogoImage } from '@/services/profile-service';
import { useAuth } from '@/hooks/use-auth';

interface ProfileImageUploadProps {
  type: 'profile' | 'logo';
  currentImageUrl?: string | null;
  onUploadComplete?: (url: string) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ProfileImageUpload({
  type,
  currentImageUrl,
  onUploadComplete,
  size = 'md',
  className,
}: ProfileImageUploadProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-20 h-20',
    lg: 'w-28 h-28',
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file (JPG, PNG, etc.)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be less than 5MB',
        variant: 'destructive',
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    if (!user) {
      toast({
        title: 'Not authenticated',
        description: 'Please sign in to upload images',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      const uploadFn = type === 'profile' ? uploadProfileImage : uploadLogoImage;
      const result = await uploadFn(user.id, file);

      if (result.success && result.url) {
        setPreview(result.url);
        onUploadComplete?.(result.url);
        toast({
          title: 'Image uploaded',
          description: `${type === 'profile' ? 'Profile' : 'Logo'} image updated successfully`,
        });
      } else {
        toast({
          title: 'Upload failed',
          description: result.error || 'Failed to upload image',
          variant: 'destructive',
        });
        // Reset preview on error
        setPreview(currentImageUrl || null);
      }
    } catch (error: any) {
      console.error('[ProfileImageUpload] Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error?.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      setPreview(currentImageUrl || null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(null);
    // TODO: Add API call to remove image from storage and update profile
    toast({
      title: 'Image removed',
      description: 'Image will be removed when you save your profile',
    });
  };

  return (
    <div className={`relative ${className}`}>
      <div className={`${sizeClasses[size]} relative rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100`}>
        {preview ? (
          <img
            src={preview}
            alt={type === 'profile' ? 'Profile' : 'Logo'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Upload className="w-6 h-6" />
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        {/* Upload overlay */}
        {!uploading && (
          <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100 group">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-white hover:text-white hover:bg-transparent"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Remove button (only show if image exists) */}
      {preview && !uploading && (
        <button
          type="button"
          onClick={handleRemove}
          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
          aria-label="Remove image"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );
}




